import { t } from "@lingui/macro";
import {
  AddManualAccountMoveDocument,
  type AddManualAccountMoveMutation,
  type AddManualAccountMoveMutationVariables,
  type Currency,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { toApiDate } from "@src/utils/dates";
import { denominate } from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { DateRange } from "@src/utils/types";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

export class AddManualMoveModalStore implements BaseStore {
  disclosure = new DisclosureState({
    onOpen: () => {
      this.form.$.currencyId.onChange(
        this.appStore.workspaceStore.settings?.currency.id ?? "",
      );
      if (!this.bankAccountOptions.length || this.bankAccountOptions.length > 1)
        return;
      this.form.$.bankAccountId.onChange(this.bankAccountOptions[0].value);
    },
    onClose: () => {
      this.form.reset();
    },
  });
  mutator = new MutationHelper<
    AddManualAccountMoveMutation,
    AddManualAccountMoveMutationVariables
  >(AddManualAccountMoveDocument);

  form = new FormState({
    date: new FieldState<Date>(new Date()).validators(required),
    bankAccountId: new FieldState(""),
    note: new FieldState(""),
    currencyId: new FieldState("").validators(required),
    amount: new FieldState("").validators(required),
  });
  currencyOptions: IOption[];
  @observable.ref bankAccountOptions: IOption[] = [];

  constructor(public appStore: AppStore) {
    makeObservable(this);
    this.currencyOptions = mapToOptions.currencies(
      appStore.workspaceStore.currencies,
    );
  }

  @computed get selectedCurrency(): Currency | undefined {
    return this.appStore.workspaceStore.currencies.find(
      (currency) => currency.id === this.form.$.currencyId.value,
    );
  }

  @action.bound setBankAccountOptions(options: IOption[]) {
    this.bankAccountOptions = options;
  }

  @action.bound async handleConfirm(): Promise<boolean> {
    const { hasError } = await this.form.validate();

    if (hasError) return false;

    const [_, error] = await this.mutator.mutate({
      date: toApiDate(this.form.$.date.$),
      note: this.form.$.note.$,
      currencyId: this.form.$.currencyId.$,
      amount: denominate(
        Number(this.form.$.amount.$),
        this.selectedCurrency?.denomination ?? 2,
      ),
    });

    if (error) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again.`,
      });
      return false;
    }

    this.disclosure.onClose();
    return true;
  }

  @action.bound handleSelectDate(range: DateRange | undefined) {
    this.form.$.date.onChange(range?.start ?? new Date());
  }
}
